import { WHITELABELS_ENDPOINT } from '@/constants/endpoints';
import { DEFAULT_SCREENS_BY_ROLE, DEFAULT_USER } from '@/constants/userManagement/userForm';
import { fetchClients } from '@/helpers/axios';
import { getUsersFilter } from '@/helpers/userManagement';
import {
  formatClientsResponseData,
  formatScreensReferenceResponseData,
  formatUserResponseData,
  formatUsersResponseData,
} from '@/helpers/userManagement/slice';
import { formatWhitelabelsResponse } from '@/helpers/userManagement/whitelabels';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';

const USERS_ENDPOINT = '/users';
const USER_ENDPOINT = '/users/{id}';
const SCREENS_BY_ROLE_ENDPOINT = '/reference/screens';

const initialState = {
  clients: [],
  users: [],
  whitelabels: [],
  orgs: [],
  selectedUser: DEFAULT_USER,
  selectedUserId: null,
  usersTotal: 0,
  filters: {
    whitelabels: [],
    clients: [],
    advertisers: [],
    userRoles: null,
    page: 1,
    limit: 15,
    sort: null,
    search: '',
  },
  usersRequestId: null,
  clientsRequestId: null,
  selectedUserRequestId: null,
  screensByRole: DEFAULT_SCREENS_BY_ROLE,
  screensByRoleRequestId: null,
  screensByRoleLoaded: false,
  whitelabelsRequestId: null,
};

export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    resetSelectedUser: state => {
      state.selectedUserId = null;
      state.selectedUser = DEFAULT_USER;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
    setSelectedUserId: (state, action) => {
      state.selectedUserId = action.payload.selectedUserId;
    },
    resetUserManagementPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getClients.pending, pendingCaseHandler('clients'))
      .addCase(getClients.rejected, rejectedCaseHandler('clients'))
      .addCase(getClients.fulfilled, fulfilledCaseHandler('clients'))
      .addCase(getUsers.pending, pendingCaseHandler('users'))
      .addCase(getUsers.rejected, rejectedCaseHandler('users'))
      .addCase(getUsers.fulfilled, (state, action) => {
        if (state.usersRequestId !== action.meta.requestId) return;
        state.usersRequestId = null;
        state.users = action.payload?.users;
        state.usersTotal = action.payload?.usersTotal;
        if (!action.payload.users?.length && state.filters.page > 1) {
          state.filters.page = 1;
        }
      })
      .addCase(getSelectedUser.pending, pendingCaseHandler('selectedUser'))
      .addCase(getSelectedUser.rejected, rejectedCaseHandler('selectedUser'))
      .addCase(getSelectedUser.fulfilled, fulfilledCaseHandler('selectedUser'))
      .addCase(getScreensByRole.pending, pendingCaseHandler('screensByRole'))
      .addCase(getScreensByRole.rejected, fulfilledCaseHandler('screensByRole'))
      .addCase(getScreensByRole.fulfilled, (state, action) => {
        state.screensByRole = action.payload;
        state.screensByRoleRequestId = false;
        state.screensByRoleLoaded = true;
      })
      .addCase(getWhitelabels.pending, pendingCaseHandler('whitelabels'))
      .addCase(getWhitelabels.rejected, rejectedCaseHandler('whitelabels'))
      .addCase(getWhitelabels.fulfilled, (state, action) => {
        if (state.whitelabelsRequestId === action.meta.requestId) {
          state.whitelabelsRequestId = null;
          state.whitelabels = action.payload.whitelabels;
          state.orgs = action.payload.orgs;
        }
      });
  },
});

export const { resetSelectedUser, setFilters, setSelectedUserId, resetUserManagementPageState } =
  userManagementSlice.actions;

export const getClients = createAsyncThunk('userManagementSlice/getClients', async payload => {
  const response = await fetchClients(null, payload?.signal);
  return formatClientsResponseData(response);
});

export const getWhitelabels = createAsyncThunk('userManagementSlice/getWhitelabels', async () => {
  const response = await axios.get(WHITELABELS_ENDPOINT, { params: { loadRelations: true, limit: 1000 } });
  const paginatedResponse = response.data;
  return formatWhitelabelsResponse(paginatedResponse.data);
});

export const getUsers = createAsyncThunk('userManagementSlice/getUsers', async (_payload, { getState }) => {
  const {
    userManagement: { filters },
  } = getState();
  const { limit, page, userRoles, clients, advertisers, whitelabels, search, sort } = filters;
  const whitelabelIds = whitelabels.map(wl => wl.value);
  const clientIds = clients.map(client => client.value);
  const advertiserIds = advertisers.map(client => client.value);

  const response = await axios.get(USERS_ENDPOINT, {
    params: {
      limit,
      page,
      search: search || undefined,
      sort,
      ...getUsersFilter(clientIds, advertiserIds, userRoles, whitelabelIds),
    },
  });
  return formatUsersResponseData(response);
});

export const getSelectedUser = createAsyncThunk(
  'userManagementSlice/getSelectedUser',
  async (_payload, { getState }) => {
    const {
      userManagement: { selectedUserId, clients, whitelabels, orgs },
      me: { role: meRole, whitelabel: meWhitelabelId },
    } = getState();
    const response = await axios.get(USER_ENDPOINT.replace('{id}', selectedUserId));
    return formatUserResponseData(response.data, orgs, whitelabels, clients, meRole, meWhitelabelId);
  },
);

export const getScreensByRole = createAsyncThunk('userManagementSlice/getScreensByRole', async () => {
  const response = await axios.get(SCREENS_BY_ROLE_ENDPOINT);
  return formatScreensReferenceResponseData(response);
});

export default userManagementSlice.reducer;
